import { z } from 'zod';
import { ROLES } from '../catalogos/acceso.js';
/**
 * Tratamiento con el que se dirige la interfaz a la persona.
 *
 * Existe como campo porque el sistema anterior imprimia "Dr." fijo para todos
 * los roles y para cualquier persona: leia mal para una medica, y peor todavia
 * para quien no es medico.
 */
export const TRATAMIENTOS = ['Dr.', 'Dra.', 'Sr.', 'Sra.', 'Srta.', ''];
export const esquemaContraseña = z
    .string()
    .min(12, 'Al menos 12 caracteres')
    .max(200)
    .refine((valor) => /[a-z]/.test(valor) && /[A-Z]/.test(valor) && /\d/.test(valor), {
    message: 'Incluya minúsculas, mayúsculas y al menos un número',
});
/**
 * Ingreso.
 *
 * El `slug` es opcional a proposito: la misma persona puede trabajar en dos
 * consultorios con el mismo correo (hospital y consulta privada es un caso
 * real). Si las credenciales valen para mas de uno, la API pide elegir; el
 * cliente reintenta con el slug. La contrasena se verifica ANTES de revelar
 * cualquier consultorio, para no permitir enumerarlos.
 */
export const esquemaIngreso = z.object({
    correo: z.string().trim().toLowerCase().email(),
    contrasena: z.string().min(1, 'Escriba su contraseña'),
    slug: z.string().trim().toLowerCase().optional(),
});
export const esquemaCrearUsuario = z
    .object({
    nombre: z.string().trim().min(2).max(120),
    correo: z.string().trim().toLowerCase().email(),
    contrasena: esquemaContraseña,
    rol: z.enum(ROLES),
    tratamiento: z.enum(TRATAMIENTOS).default(''),
    cedulaProfesional: z.string().trim().max(30).default(''),
    especialidad: z.string().trim().max(120).default(''),
    telefono: z.string().trim().max(40).default(''),
})
    .superRefine((valores, ctx) => {
    // Quien firma documentos clinicos necesita cedula: sin ella el documento
    // impreso queda invalido.
    if (valores.rol !== 'asistente' && !valores.cedulaProfesional) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['cedulaProfesional'],
            message: 'La cédula profesional es obligatoria para quien firma documentos',
        });
    }
});
export const esquemaActualizarUsuario = z.object({
    nombre: z.string().trim().min(2).max(120).optional(),
    rol: z.enum(ROLES).optional(),
    tratamiento: z.enum(TRATAMIENTOS).optional(),
    cedulaProfesional: z.string().trim().max(30).optional(),
    especialidad: z.string().trim().max(120).optional(),
    telefono: z.string().trim().max(40).optional(),
    activo: z.boolean().optional(),
});
/**
 * Contrasena provisional que el propietario asigna a otra cuenta, p. ej. si la
 * persona olvido la suya. Quien la recibe tiene que cambiarla al entrar.
 */
export const esquemaAsignarContrasenaProvisional = z.object({
    contrasena: esquemaContraseña,
});
export const esquemaCambiarContraseña = z.object({
    contrasenaActual: z.string().min(1),
    contrasenaNueva: esquemaContraseña,
});
/**
 * Pedir un enlace para restablecer la contrasena. `slug` acota a un consultorio
 * cuando se entra por su direccion; sin el, se envia un enlace por cada
 * consultorio en el que exista el correo.
 */
export const esquemaSolicitarRestablecimiento = z.object({
    correo: z.string().trim().toLowerCase().email('Revise el correo: falta algo'),
    slug: z.string().trim().toLowerCase().optional(),
});
/** Poner la contrasena nueva con el token del enlace. */
export const esquemaConfirmarRestablecimiento = z.object({
    token: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9_-]{32,128}$/, 'El enlace no es válido'),
    contrasenaNueva: esquemaContraseña,
});
/** Nombre para mostrar, con tratamiento cuando existe. */
export function nombreParaMostrar(usuario) {
    return usuario.tratamiento ? `${usuario.tratamiento} ${usuario.nombre}` : usuario.nombre;
}
//# sourceMappingURL=usuario.js.map